import React, { useState, useContext } from 'react';
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  Snackbar,
  Alert,
  CircularProgress,
  Grid,
} from '@mui/material';
import { getDatabase, ref, push } from 'firebase/database';

import { app } from 'clients/firebaseClient';
import { LayoutContext } from 'contexts/LayoutContext/LayoutContext';
import { avantGardeFont } from 'styles/muiTheme';


const BookCover3D = ({ isMobile }: { isMobile: boolean }) => {
  const bookWidth = isMobile ? 252 : 378;
  const bookHeight = isMobile ? 360 : 540;
  const spineWidth = isMobile ? 36 : 50;

  return (
    <Box
      sx={{
        perspective: '1500px',
        width: bookWidth + spineWidth + 40,
        height: bookHeight + 60,
        mx: 'auto',
        mt: -12,
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          height: '100%',
          transformStyle: 'preserve-3d',
          transform: 'rotateY(-25deg) rotateX(5deg)',
          ml: 4,
        }}
      >
        {/* Front Cover */}
        <Box
          sx={{
            position: 'absolute',
            width: bookWidth,
            height: bookHeight,
            background: '#000',
            borderRadius: '0 6px 6px 0',
            transform: `translateZ(${spineWidth / 2}px)`,
            boxShadow: '8px 8px 30px rgba(0,0,0,0.6), 2px 2px 8px rgba(0,0,0,0.4)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 4,
            pt: 6,
            pl: 3,
            overflow: 'hidden',
            // Hardcover raised edge
            border: '1px solid rgba(255,255,255,0.04)',
          }}
        >
          {/* Subtle cloth/linen texture */}
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              opacity: 0.03,
              background:
                `repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(255,255,255,0.1) 2px, rgba(255,255,255,0.1) 3px),
                 repeating-linear-gradient(90deg, transparent, transparent 2px, rgba(255,255,255,0.1) 2px, rgba(255,255,255,0.1) 3px)`,
            }}
          />

          {/* Embossed inner border */}
          <Box
            sx={{
              position: 'absolute',
              top: isMobile ? 12 : 18,
              left: isMobile ? 12 : 18,
              right: isMobile ? 12 : 18,
              bottom: isMobile ? 12 : 18,
              border: '1px solid rgba(255,255,255,0.06)',
              borderRadius: 1,
              pointerEvents: 'none',
            }}
          />

          <Box sx={{ position: 'relative', zIndex: 1, mt: 2, px: 1, width: '100%', boxSizing: 'border-box', display: 'flex', flexDirection: 'column', gap: '2px' }}>
            {[
              { text: 'Doing Things', fontWeight: 500, fullWidth: true },
              { text: "That Don't", fontWeight: 400, fullWidth: false },
              { text: 'Penetrate', fontWeight: 500, fullWidth: true },
              { text: 'A Market', fontWeight: 500, fullWidth: true },
              { text: 'Is Just', fontWeight: 400, fullWidth: false },
              { text: 'M*sturbating', fontWeight: 700, fullWidth: true },
            ].map(({ text, fontWeight, fullWidth }) => (
              <Typography
                key={text}
                sx={{
                  fontFamily: `${avantGardeFont}, sans-serif`,
                  color: '#fff',
                  fontWeight,
                  textTransform: 'uppercase',
                  lineHeight: 1,
                  whiteSpace: 'nowrap',
                  ...(fullWidth ? {
                    fontSize: '10rem',
                    width: 'fit-content',
                    transformOrigin: 'left top',
                  } : {
                    fontSize: isMobile ? '0.9rem' : '1.3rem',
                    textAlign: 'center',
                    letterSpacing: 4,
                    py: 0.5,
                    height: 'auto',
                  }),
                }}
                ref={(el: HTMLElement | null) => {
                  if (!el || !fullWidth) return;
                  const resize = () => {
                    const parent = el.parentElement;
                    if (!parent) return;
                    const availableWidth = parent.clientWidth;
                    // Reset transform and height to get natural measurements
                    el.style.transform = 'none';
                    el.style.height = 'auto';
                    const textWidth = el.scrollWidth;
                    const textHeight = el.scrollHeight;
                    const ratio = availableWidth / textWidth;
                    el.style.transform = `scale(${ratio})`;
                    el.style.height = `${textHeight * ratio}px`;
                  };
                  resize();
                  window.addEventListener('resize', resize);
                  document.fonts.ready.then(resize);
                }}
              >
                {text}
              </Typography>
            ))}
          </Box>


          <Box sx={{ position: 'relative', zIndex: 1, textAlign: 'center', mb: isMobile ? 3 : 5 }}>
            <Typography
              sx={{
                fontFamily: `${avantGardeFont}, sans-serif`,
                color: 'rgba(255,255,255,0.5)',
                fontSize: isMobile ? '0.6rem' : '0.75rem',
                textTransform: 'uppercase',
                letterSpacing: 4,
                mb: 0.5,
                mt: 1,
                fontWeight: 300,
              }}
            >
              by
            </Typography>
            <Typography
              sx={{
                fontFamily: `${avantGardeFont}, sans-serif`,
                color: '#fff',
                fontSize: isMobile ? '0.85rem' : '1.1rem',
                letterSpacing: 3,
                fontWeight: 300,
              }}
            >
              Marco Yammine
            </Typography>
          </Box>
        </Box>

        {/* Spine */}
        <Box
          sx={{
            position: 'absolute',
            width: spineWidth,
            height: bookHeight,
            background: '#000',
            transform: `rotateY(90deg) translateZ(0px) translateX(-${spineWidth / 2}px)`,
            transformOrigin: 'left center',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            // Hardcover spine ridges
            borderLeft: '1px solid rgba(255,255,255,0.05)',
            borderRight: '1px solid rgba(255,255,255,0.05)',
            boxShadow: 'inset 2px 0 4px rgba(255,255,255,0.03), inset -2px 0 4px rgba(255,255,255,0.03)',
          }}
        >
          <Typography
            sx={{
              fontFamily: `${avantGardeFont}, sans-serif`,
              color: '#fff',
              fontWeight: 700,
              fontSize: isMobile ? '0.45rem' : '0.6rem',
              textTransform: 'uppercase',
              letterSpacing: 2,
              writingMode: 'vertical-rl',
              transform: 'rotate(180deg)',
              whiteSpace: 'nowrap',
            }}
          >
            Marco Yammine
          </Typography>
        </Box>

        {/* Back Cover */}
        <Box
          sx={{
            position: 'absolute',
            width: bookWidth,
            height: bookHeight,
            background: '#000',
            borderRadius: '6px 0 0 6px',
            transform: `translateZ(-${spineWidth / 2}px)`,
            boxShadow: '-4px 4px 20px rgba(0,0,0,0.4)',
          }}
        />

        {/* Page edges (side/right) */}
        <Box
          sx={{
            position: 'absolute',
            width: spineWidth - 6,
            height: bookHeight - 16,
            top: 8,
            background: 'repeating-linear-gradient(0deg, #f0efe8, #f0efe8 1px, #e0dfd8 1px, #e0dfd8 2px)',
            transform: `rotateY(90deg) translateZ(${bookWidth - 2}px) translateX(-${(spineWidth - 6) / 2}px)`,
            transformOrigin: 'left center',
          }}
        />

      </Box>
    </Box>
  );
};


export const Book = () => {
  const { isMobile } = useContext(LayoutContext);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;

    setLoading(true);
    try {
      const db = getDatabase(app);
      const waitlistRef = ref(db, 'book-waitlist');
      await push(waitlistRef, {
        email,
        name: name || null,
        timestamp: Date.now(),
      });
      setSnackbar({ open: true, message: 'You\'re on the list! We\'ll notify you when the book launches.', severity: 'success' });
      setEmail('');
      setName('');
    } catch {
      setSnackbar({ open: true, message: 'Something went wrong. Please try again.', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container
      maxWidth="xl"
      sx={{
        textAlign: 'center',
        pt: isMobile ? 16 : 20,
        pb: 10,
        minHeight: '100vh',
        position: 'relative',
        zIndex: 1,
      }}
    >
      <BookCover3D isMobile={isMobile} />

      <Typography
        variant="h6"
        sx={{ mt: -3, mb: 2, maxWidth: '80vw', mx: 'auto' }}
      >
        A no-BS guide for people who want to create real value.
      </Typography>

      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          mx: 'auto',
          p: 4,
          borderRadius: 3,
          background: 'rgba(99, 101, 255, 0.06)',
          border: `1px solid rgba(99, 101, 255, 0.15)`,
          maxWidth: 600,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
          Join the Waitlist
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Name"
              variant="outlined"
              size="small"
              value={name}
              fullWidth
              onChange={(e) => setName(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Email"
              variant="outlined"
              size="small"
              type="email"
              required
              value={email}
              fullWidth
              onChange={(e) => setEmail(e.target.value)}
            />
          </Grid>
        </Grid>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            color="primary"
            href="/home"
            sx={{ flex: 1, textDecoration: 'none' }}
          >
            Visit Website
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="secondary"
            disabled={loading || !email}
            sx={{
              flex: 1,
              '&.Mui-disabled': {
                backgroundColor: 'rgba(128, 0, 212, 0.4)',
                color: 'rgba(255, 255, 255, 0.5)',
              },
            }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Notify Me'}
          </Button>
        </Box>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};
